import { z } from 'zod';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { WriteBackToLeverStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { WriteBackToFountainStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';
import { WriteBackToGreenhouseStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/greenhouse-write-back';
import { WriteBackToWorkdayViaWsStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/workday-write-back';
import { WriteBackToAdpStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/adp-write-back';
import { WriteBackToAshbyStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/ashby-write-back';
import { HiringCopilotModelByJobTitleConfig } from 'libs/end-to-end/src/types/databases/scheduling/hiring-copilot-model-by-job-title-config';

export const WorkflowHiringReadinessScoreWriteBackStrategy = z.union([
  WriteBackToFountainStrategy,
  WriteBackToLeverStrategy,
  WriteBackToGreenhouseStrategy,
  WriteBackToWorkdayViaWsStrategy,
  WriteBackToAdpStrategy,
  WriteBackToAshbyStrategy,
]);

export type WorkflowHiringReadinessScoreWriteBackStrategy = z.infer<
  typeof WorkflowHiringReadinessScoreWriteBackStrategy
>;

export const WriteBackOverrideRuleComparator = z.enum([
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
  'equalTo',
]);

export type WriteBackOverrideRuleComparator = z.infer<
  typeof WriteBackOverrideRuleComparator
>;

/**
 * A single condition for a rule (comparator + threshold)
 */
export const WriteBackOverrideRuleCondition = z.object({
  /**
   * The comparison operator to use
   */
  comparator: WriteBackOverrideRuleComparator,

  /**
   * The threshold value to compare against
   */
  thresholdValue: z.number(),
});

export type WriteBackOverrideRuleCondition = z.infer<
  typeof WriteBackOverrideRuleCondition
>;

/**
 * Transform the score with a math operation (multiply or divide by a factor).
 */
export const WriteBackOverrideRuleTransformation = z.object({
  operation: z.enum(['multiply', 'divide']),
  factor: z.number(),
});

export type WriteBackOverrideRuleTransformation = z.infer<
  typeof WriteBackOverrideRuleTransformation
>;

/**
 * A rule that overrides the writeback value based on conditions.
 * If the score meets all conditions, either the overrideValue is written back,
 * or the score is transformed (multiply/divide by factor).
 */
export const WriteBackOverrideRule = z
  .object({
    /**
     * The conditions to evaluate (all must be satisfied for the rule to match)
     */
    conditions: z.array(WriteBackOverrideRuleCondition),

    /**
     * The value to write back if all conditions are met (can be a number or string).
     * Omit when using transformation instead.
     */
    overrideValue: z.union([z.number(), z.string()]).optional(),

    /**
     * When set, the score is transformed by multiplying or dividing by factor instead of using a fixed override value.
     */
    transformation: WriteBackOverrideRuleTransformation.optional(),
  })
  .refine(
    (data) =>
      data.overrideValue !== undefined || data.transformation !== undefined,
    { message: 'Either overrideValue or transformation must be set' },
  );

export type WriteBackOverrideRule = z.infer<typeof WriteBackOverrideRule>;

export const WorkflowHiringReadinessScoreWriteBackV2ConfigOption = z.object({
  groupName: z.string(),
  strategy: WorkflowHiringReadinessScoreWriteBackStrategy.optional(),
  mode: WorkflowWriteBackMode,

  /**
   * Optional list of rules to override the writeback value based on the score.
   * Rules are evaluated in order, and the first matching rule is applied.
   */
  overrideRules: z.array(WriteBackOverrideRule),
});

export type WorkflowHiringReadinessScoreWriteBackV2ConfigOption = z.infer<
  typeof WorkflowHiringReadinessScoreWriteBackV2ConfigOption
>;

export const WorkflowHiringReadinessScoreWriteBackV2Config = z.object({
  options: z.array(WorkflowHiringReadinessScoreWriteBackV2ConfigOption),

  /**
   * Optional fallback configuration for applicants without a group or with an empty group.
   * If not provided, applicants without groups will be skipped.
   */
  noGroupConfig: WorkflowHiringReadinessScoreWriteBackV2ConfigOption.omit({
    groupName: true,
  }).optional(),
});

export type WorkflowHiringReadinessScoreWriteBackV2Config = z.infer<
  typeof WorkflowHiringReadinessScoreWriteBackV2Config
>;

/**
 * Details for a workflow that writes back hiring readiness scores.
 */
export const WorkflowHiringReadinessScoreWriteBackDetails = z.object({
  type: z.literal('hiringReadinessScoreWriteBack'),

  /**
   * The strategy for writing back.
   */
  strategy: WorkflowHiringReadinessScoreWriteBackStrategy.optional(),

  /**
   * The mode for writing back.
   */
  mode: WorkflowWriteBackMode.optional(),

  /**
   * V2 Write Back Config
   */
  v2Config: WorkflowHiringReadinessScoreWriteBackV2Config.optional(),

  /**
   * The number of previous days of applicants to consider for writing back.
   */
  numPreviousDaysOfApplicants: z.number().optional(),

  /**
   * The minimum readiness rating for an applicant to be considered for writing back.
   */
  minReadinessRating: z.number().optional(),

  /**
   * Number of samples to send
   */
  numberOfSamplesToSend: z.number().optional(),

  /**
   * When true, also skip write back when a prior successful payload hash exists
   * for this entity on this schedule (duplicate payload is always skipped).
   */
  skipWriteBackIfDuplicate: z.boolean().optional(),

  /**
   * Optional model-by-job-title (or job group) config for hiring copilot.
   */
  hiringCopilotModelByJobTitleConfig:
    HiringCopilotModelByJobTitleConfig.optional(),
});

export type WorkflowHiringReadinessScoreWriteBackDetails = z.infer<
  typeof WorkflowHiringReadinessScoreWriteBackDetails
>;

/**
 * Helper function to check if a single condition matches.
 */
const evaluateCondition = (
  readinessRating: number,
  condition: WriteBackOverrideRuleCondition,
): boolean => {
  const { comparator, thresholdValue } = condition;

  switch (comparator) {
    case 'greaterThan':
      return readinessRating > thresholdValue;
    case 'greaterThanOrEqual':
      return readinessRating >= thresholdValue;
    case 'lessThan':
      return readinessRating < thresholdValue;
    case 'lessThanOrEqual':
      return readinessRating <= thresholdValue;
    case 'equalTo':
      return readinessRating === thresholdValue;
    default:
      return false;
  }
};

/**
 * Helper function to check if a specific rule matches a given readiness rating.
 * Useful for UI visualization to show which rules are matching.
 */
export const checkIfRuleMatches = ({
  readinessRating,
  rule,
}: {
  readinessRating: number;
  rule: WriteBackOverrideRule;
}): boolean => {
  if (rule.conditions.length === 0) {
    return false;
  }

  return rule.conditions.every((condition) =>
    evaluateCondition(readinessRating, condition),
  );
};

export const evaluateReadinessBasedOnOverrideRules = ({
  readinessRating,
  overrideRules,
}: {
  readinessRating: number;
  overrideRules: WriteBackOverrideRule[];
}): string | number => {
  for (const rule of overrideRules) {
    if (checkIfRuleMatches({ readinessRating, rule })) {
      if (rule.transformation) {
        const { operation, factor } = rule.transformation;
        if (factor === 0 && operation === 'divide') {
          return readinessRating;
        }
        const result =
          operation === 'multiply'
            ? readinessRating * factor
            : readinessRating / factor;
        return result;
      } else {
        return rule.overrideValue ?? readinessRating;
      }
    }
  }
  return readinessRating;
};

/**
 * Determines which configuration to use for an applicant based on their group assignment.
 * This handles both V2 (group-based) and V1 (legacy) configurations.
 */
export const determineHiringReadinessApplicantConfig = ({
  groupName,
  v2Config,
  legacyStrategy,
  legacyMode,
}: {
  groupName: string | undefined;
  v2Config: WorkflowHiringReadinessScoreWriteBackV2Config | undefined;
  legacyStrategy?: WorkflowHiringReadinessScoreWriteBackStrategy;
  legacyMode?: WorkflowWriteBackMode;
}):
  | {
      type: 'success';
      strategy: WorkflowHiringReadinessScoreWriteBackStrategy;
      mode: WorkflowWriteBackMode;
      overrideRules: WriteBackOverrideRule[];
      groupName: string;
      isNoGroupFallback: boolean;
    }
  | { type: 'skip'; reason: string }
  | { type: 'error'; reason: string } => {
  // V2 Configuration (group-based)
  if (v2Config) {
    const trimmedGroup = groupName?.trim() || '';

    // Check if no group or empty group
    if (!trimmedGroup) {
      if (v2Config.noGroupConfig && v2Config.noGroupConfig.strategy) {
        return {
          type: 'success',
          strategy: v2Config.noGroupConfig.strategy,
          mode: v2Config.noGroupConfig.mode,
          overrideRules: v2Config.noGroupConfig.overrideRules ?? [],
          groupName: '(No Group - Using Fallback)',
          isNoGroupFallback: true,
        };
      }
      return {
        type: 'skip',
        reason: 'No group assigned and no fallback configuration',
      };
    }

    // Find matching group
    const matchingGroup = v2Config.options.find(
      (opt) => opt.groupName === trimmedGroup,
    );

    if (!matchingGroup) {
      return {
        type: 'error',
        reason: `Group "${trimmedGroup}" not found in configuration`,
      };
    }

    if (!matchingGroup.strategy) {
      return {
        type: 'skip',
        reason: `Group "${trimmedGroup}" has no strategy configured`,
      };
    }

    return {
      type: 'success',
      strategy: matchingGroup.strategy,
      mode: matchingGroup.mode,
      overrideRules: matchingGroup.overrideRules ?? [],
      groupName: matchingGroup.groupName,
      isNoGroupFallback: false,
    };
  }

  // V1 Configuration (legacy)
  if (legacyStrategy && legacyMode) {
    return {
      type: 'success',
      strategy: legacyStrategy,
      mode: legacyMode,
      overrideRules: [],
      groupName: '(Legacy Configuration)',
      isNoGroupFallback: false,
    };
  }

  return {
    type: 'skip',
    reason: 'No configuration available',
  };
};
